import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const ANDROID_URL =
  "https://play.google.com/store/apps/details?id=com.mas10";
const IOS_URL = "https://apps.apple.com/ar/app/10/id1575209914";

type DownloadModalProps = {
  open: boolean;
  onClose: () => void;
};

function GooglePlayLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M3.6 2.2c-.4.2-.6.6-.6 1.1v17.4c0 .5.2.9.6 1.1l9.7-9.8L3.6 2.2z"
      />
      <path
        fill="currentColor"
        d="M14.3 13l-1.8-1.8 1.8-1.9 4.5 2.6c.5.3.5 1 0 1.3L14.3 13z"
        opacity="0.85"
      />
      <path
        fill="currentColor"
        d="M13.4 11.2 3.6 2.2c.2-.1.4-.2.7-.2.3 0 .6.1.9.3l10.3 5.9-2.1 3z"
        opacity="0.7"
      />
      <path
        fill="currentColor"
        d="M13.4 12.8l2.1 3c-.3.2-.6.3-.9.3-.3 0-.5-.1-.7-.2l-9.7-5.6 9.2 2.5z"
        opacity="0.55"
      />
    </svg>
  );
}

function AppleLogo({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M16.7 12.6c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.6-1.3-.1-2.5.8-3.1.8-.7 0-1.7-.7-2.8-.7-1.4 0-2.8.9-3.5 2.2-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.7 2.1 1.1 0 1.5-.7 2.8-.7s1.6.7 2.8.7c1.2 0 1.9-1 2.6-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.2-.8-2.2-3.8zM14.5 6.3c.6-.7 1-1.7.9-2.7-0.9.1-1.9.6-2.5 1.3-.6.6-1.1 1.6-1 2.6 1 .1 1.9-.5 2.6-1.2z"
      />
    </svg>
  );
}

export function DownloadModal({ open, onClose }: DownloadModalProps) {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <button
            type="button"
            aria-label="Cerrar"
            className="absolute inset-0 bg-brand-800/45 backdrop-blur-[2px]"
            onClick={onClose}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="download-modal-title"
            className="relative w-full max-w-md rounded-3xl border border-borderSoft bg-white p-6 shadow-soft sm:p-8"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
          >
            <button
              type="button"
              aria-label="Cerrar popup"
              onClick={onClose}
              className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-ink transition hover:bg-brand-50 hover:text-brand-700"
            >
              <X size={18} />
            </button>

            <h2
              id="download-modal-title"
              className="pr-10 text-2xl font-extrabold tracking-tight text-brand-700"
            >
              Descargá la app
            </h2>
            <p className="mt-2 text-sm text-ink sm:text-base">
              Elegí tu plataforma y empezá a potenciar tu juego.
            </p>

            <div className="mt-6 flex flex-row gap-3">
              <a
                href={ANDROID_URL}
                target="_blank"
                rel="noreferrer"
                className="btn-primary w-1/2 gap-2"
              >
                <GooglePlayLogo className="h-5 w-5 shrink-0" />
                Android
              </a>
              <a
                href={IOS_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-1/2 items-center justify-center gap-2 rounded-full border border-brand-500 bg-white px-6 py-3 text-sm font-semibold text-brand-700 shadow-soft transition duration-200 hover:-translate-y-0.5 hover:bg-brand-50 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-700 focus-visible:ring-offset-2"
              >
                <AppleLogo className="h-5 w-5 shrink-0" />
                iOS
              </a>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
